'use client'

import * as React from 'react'
import { cn } from '@/lib/utils'

// Cloudflare-style rotating H1. Each statement is a two-tone line: the lead
// clause stays ink, the payoff clause flips to a cobalt-italic accent (the
// house "display-italic" treatment). The separator is per-statement so the
// line reads cleanly — a continuation joins with a space, an apposition with
// a comma — no stray "into, measured" comma. Crossfades on a slow interval so
// the headline stays readable. Holds the first statement under reduced motion.

export const STATEMENTS: { lead: string; tail: string; sep?: string }[] = [
  // One claim at three altitudes, and the first one names the actual
  // product (Rohin: "our real stuff is Instagram and TikTok shoppable
  // videos and galleries"). Product first, outcome second, enemy third.
  // The tool count matches the sitewide CTA band ("Stop renting six
  // tools. Ship one.") so the site does one piece of arithmetic.
  { lead: 'Shoppable Instagram + TikTok', tail: 'galleries, on your store.', sep: ' ' },
  { lead: 'Turn your UGC into', tail: 'measured store revenue.', sep: ' ' },
]

// Each statement is held long enough for a reader to actually finish
// it (avg adult ~250 wpm = ~4s for ~16 words; we hold 8s + 700ms
// crossfade so even slow readers + scanners catch it).
export const HOLD_MS = 8000
export const FADE_MS = 700

// State is now lifted to HeroContent so the metrics strip can stay in sync.
// Index and fading are passed as props rather than managed internally.
export function RotatingHeadline({
  className,
  index,
  fading,
}: {
  className?: string
  index: number
  fading: boolean
}) {
  const s = STATEMENTS[index]

  // Longest statement (by character count) reserves the headline box height,
  // so rotating between short and long lines causes no layout shift (CLS).
  const longest = STATEMENTS.reduce(
    (a, b) =>
      (b.lead + (b.sep ?? ', ') + b.tail).length > (a.lead + (a.sep ?? ', ') + a.tail).length
        ? b
        : a,
    STATEMENTS[0],
  )

  return (
    <h1
      className={cn(
        // Was text-display-1 (~text-7xl). That was overwhelming the
        // viewport on the home hero. text-display-3 tokenizes the
        // ~text-5xl ad-hoc stack this used to be (clamp 2.25rem->3.75rem,
        // weight 600 baked) per DESIGN_SYSTEM.md — no more arbitrary
        // text-[N] breakpoint stack. Override at the call-site via className.
        // grid-stacked: an invisible longest-statement sizer holds the
        // height so the crossfade rotation never shifts layout.
        'grid text-balance font-display text-display-3',
        className,
      )}
      // Plus Jakarta Sans is the locked display face (font-display-hero resolves
      // to it, weight 800 via globals). It already carries the bold, high-
      // contrast weight the headline needs, so the old Alumni-Sans-Pinstripe
      // text-stroke hack was removed: on Plus Jakarta it only muddies the glyphs.
      aria-live="polite"
      aria-atomic="true"
    >
      <span aria-hidden className="invisible" style={{ gridArea: '1 / 1' }}>
        {longest.lead}
        {longest.sep ?? ', '}
        {longest.tail}
      </span>
      <span
        className="inline-block transition-opacity"
        style={{
          gridArea: '1 / 1',
          opacity: fading ? 0 : 1,
          transitionDuration: `${FADE_MS}ms`,
        }}
      >
        {s.lead}
        {s.sep ?? ', '}
        <span className="display-italic">{s.tail}</span>
      </span>
    </h1>
  )
}
